import { useEffect, useState } from "react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Heart, HandHelping, Ship, Sparkles } from "lucide-react";

/**
 * Page « Soutenir ITERCATUS » — P2.
 * Aucun paiement reel. Le provider backend est desactive : le bouton reste
 * inactif tant que /api/donations/info retourne active=false.
 */
export default function DonationsPage() {
  const [info, setInfo] = useState({ active: false, reason: "loading", suggested_amounts_cents: [], currency: "EUR" });
  const [amount, setAmount] = useState(2500);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/donations/info`);
        const data = await res.json();
        setInfo(data);
        if (Array.isArray(data.suggested_amounts_cents) && data.suggested_amounts_cents.length) {
          setAmount(data.suggested_amounts_cents[1] || data.suggested_amounts_cents[0]);
        }
      } catch {
        setInfo({ active: false, reason: "network_error", suggested_amounts_cents: [], currency: "EUR" });
      }
    })();
  }, []);

  const fmt = (cents) =>
    new Intl.NumberFormat("fr-FR", { style: "currency", currency: info.currency || "EUR" }).format((cents || 0) / 100);

  return (
    <div className="min-h-[70vh] bg-secondary" data-testid="donations-page">
      {/* Hero */}
      <section className="max-w-5xl mx-auto px-6 pt-16 md:pt-24 pb-10 text-center">
        <div className="inline-flex items-center gap-2 text-accent text-xs uppercase tracking-[0.3em] mb-4">
          <Heart className="h-4 w-4" /> Soutenir
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-primary mb-6 leading-tight">
          Soutenir ITERCATUS
        </h1>
        <p className="max-w-2xl mx-auto text-slate-600 text-base md:text-lg leading-relaxed">
          ITERCATUS est une association à but non lucratif portée par des passionnés de voyage.
          Vos dons libres nous aident à rester indépendants, à négocier de meilleurs avantages
          pour les adhérents et à produire des contenus honnêtes.
        </p>
      </section>

      {/* Value cards */}
      <section className="max-w-5xl mx-auto px-6 pb-10">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { icon: HandHelping, title: "Indépendance", desc: "Zéro pub tierce, zéro tracker publicitaire. Vos dons paient l'hébergement et les outils." },
            { icon: Ship, title: "Reportages honnêtes", desc: "Nous testons les prestations pour de vrai — un don finance un futur test terrain." },
            { icon: Sparkles, title: "Avantages adhérents", desc: "Chaque euro renforce notre poids dans les négociations d'offres exclusives." },
          ].map((c) => (
            <Card key={c.title} className="border-primary/10">
              <CardContent className="p-6">
                <c.icon className="h-6 w-6 text-accent mb-3" />
                <p className="font-heading text-lg text-primary mb-1">{c.title}</p>
                <p className="text-sm text-slate-600 leading-relaxed">{c.desc}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Donation form (disabled until provider active) */}
      <section className="max-w-2xl mx-auto px-6 pb-20">
        <Card className="border-primary/10 shadow-sm">
          <CardContent className="p-6 md:p-8">
            <p className="font-heading text-xl text-primary mb-2">Faire un don ponctuel</p>
            <p className="text-sm text-slate-600 mb-6">
              Choisissez librement le montant que vous souhaitez donner.
            </p>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
              {(info.suggested_amounts_cents || [1000, 2500, 5000, 10000]).map((a) => (
                <button
                  key={a}
                  type="button"
                  onClick={() => setAmount(a)}
                  data-testid={`donation-amount-${a}`}
                  className={`rounded-lg border py-3 text-sm font-semibold transition ${
                    amount === a
                      ? "border-accent bg-accent/10 text-primary"
                      : "border-slate-200 hover:border-accent/60 text-slate-600"
                  }`}
                >
                  {fmt(a)}
                </button>
              ))}
            </div>

            <Button
              type="button"
              disabled={!info.active}
              className="w-full h-12 rounded-full font-semibold"
              data-testid="donation-cta"
            >
              {info.active ? `Donner ${fmt(amount)}` : "Bientôt disponible"}
            </Button>

            {!info.active && (
              <p className="text-xs text-slate-500 mt-4 text-center leading-relaxed" data-testid="donation-disabled-notice">
                Le paiement en ligne des dons ouvrira très prochainement. En attendant,
                vous pouvez nous soutenir en{" "}
                <a href="/adherer" className="text-primary underline hover:text-accent">
                  adhérant à l'association
                </a>
                .
              </p>
            )}
          </CardContent>
        </Card>
      </section>
    </div>
  );
}
